import * as R from 'ramda';
import {
    isValueExist,
    formatNumber,
    listIncludes,
    generateId,
    testerFromArray,
    common,
    guaranteeArray,
} from '@util';

import {
    VIEW_DEFAULT_LAYOUT_CONFIG,
    WIDGET_ADD_TYPE,
    CONNECTION_TYPES,
    MONTH_NAMES,
} from '@consts';

import { commDateFormat, pastNDays, getDefaultDate } from '@/utils/timeUtils';

import {
    isFbSingle,
    isFbCarousel,
    isInstaSingle,
    isInstaCarousel,
    isGoogleSearch,
    isGoogleDisplayExternal,
    isGoogleDisplayInternal,
    isGoogleLegacyDisplay,
    isGoogleUniversalAppV2,
    isGoogleVideo,
    isGoogleImage,
    isApple,
    isKakao,
    isYelp,
    isAmazon,
    isNaverContentsAdInformation,
    isMs,
    isGoogleDynamicSearch,
} from '@/utils/adsUtils';

import memoize from 'lodash/memoize';
import memoizee from 'memoizee';

import { numeral } from '@util';
import moment from 'moment';
import currencyToSymbolMap from 'currency-symbol-map/map';

import { getFieldLocale } from '../field';

export const {
    metricUtils,
    editableElement,
    dashboardFilter,
    dataSourceUtils,
} = common;

export const {
    getCellType: commonGetCellType,
    getHeadersFromListForWeb,
    getWordCloudList,
} = metricUtils;

export const { getMergedDashboardFilter } = dashboardFilter;

const _propAdName = R.prop('adName');

export const getCellType = memoize(commonGetCellType);

export const CHANNEL_INFO = (() => {
    const BASES = [['creative', 'params'], ['creative']];

    const realPaths = [
        ['thumbnail'],
        ['image'],
        ['media'],
        ['medias', 0, 'url'],
        ['image_url'],
        ['imageUrl'],
        ['cards', 0, 'media'],
        ['media', 'youtubeVideoId'],
        ['mobileAppInfo', 'logo'],
        ['media_file'],
        ['media', 'download_link'], // snapchat
    ];

    let _paths = R.xprod(BASES, realPaths);
    _paths = _paths.map(R.apply(R.concat));

    const image = (data) => {
        const _fromData = R.path(R.__, data);

        for (let path of _paths) {
            const data = _fromData(path);
            if (data && typeof data === 'string') {
                return data;
            }
        }

        return '';
    };

    const FORMAT = (data = {}) => {
        return {
            ...data,
            creative: formatCreative(data.creativeType, data.creative),
        };
    };

    const _channelInfoBasicFunc = () => ({
        content: R.compose(R.pathOr('', ['creative', 'description']), FORMAT),
        image: R.compose(image, FORMAT),
    });

    return R.cond([
        [isFbSingle, _channelInfoBasicFunc],
        [
            isFbCarousel,
            () => ({
                content: R.pathOr('', ['creative', 'description']),
                image,
            }),
        ],
        [isInstaSingle, _channelInfoBasicFunc],
        [
            isInstaCarousel,
            () => ({
                content: R.pathOr('', ['creative', 'description']),
                image,
                withoutDest: true,
            }),
        ],

        [isGoogleSearch, _channelInfoBasicFunc],
        [
            R.anyPass([isGoogleDisplayInternal, isGoogleDisplayExternal]),
            () => ({
                content: R.pathOr('', ['creative', 'titles', 0]),
                image,
            }),
        ],
        [
            isGoogleLegacyDisplay,
            () => ({
                content: R.pathOr('', ['creative', 'shortTitle']),
                image,
            }),
        ],
        [
            isGoogleUniversalAppV2,
            () => ({
                content: R.pathOr('', ['creative', 'descriptions', 0]),
                image,
            }),
        ],
        [
            isGoogleVideo,
            () => ({
                content: R.pathOr('', ['name']),
                image: (data = {}) => {
                    let videoId;
                    let { media = {} } = formatCreative('', data.creative);

                    if (typeof media === 'object' && media.youtubeVideoId) {
                        videoId = media.youtubeVideoId;
                    } else if (typeof media === 'string') {
                        // eslint-disable-next-line no-unused-vars
                        const [_, id] = media.split('?v=') || [];

                        videoId = id;
                    }

                    if (videoId) {
                        return `https://img.youtube.com/vi/${videoId}/0.jpg`;
                    }
                },
                withoutDest: true,
            }),
        ],
        [
            isGoogleImage,
            () => ({
                content: R.compose(
                    R.pathOr('', ['data', 'value']),
                    _propAdName,
                    R.propOr({}, 'metric')
                ),
                image,
            }),
        ],
        [
            isApple,
            () => ({
                content: R.pathOr('', [
                    'creative',
                    'mobileAppInfo',
                    'description',
                ]),
                image,
            }),
        ],
        [
            isKakao,
            () => ({
                content: R.compose(
                    R.pathOr('', ['data', 'value']),
                    _propAdName,
                    R.propOr({}, 'metric')
                ),
                image,
            }),
        ],
        [
            isMs,
            () => ({
                content: R.pathOr('', ['creative', 'text1']),
                image,
            }),
        ],
        [
            isYelp,
            () => ({
                content: R.pathOr('', ['creative', 'title']),
                image: image,
            }),
        ],
        [
            isAmazon,
            () => ({
                content: R.compose(
                    R.pathOr('', ['data', 'value']),
                    _propAdName,
                    R.prop('metric')
                ),
                image,
            }),
        ],
        [
            isNaverContentsAdInformation,
            () => ({
                content: R.pathOr('', ['creative', 'headline']),
                image({ creative = {} }) {
                    return (
                        creative.imagePath ||
                        R.pathOr('', ['params', 'imagePath'], creative)
                    );
                },
            }),
        ],
        [isGoogleDynamicSearch, _channelInfoBasicFunc],
        [
            R.T,
            () => {
                return {
                    content: R.pathOr('', ['creative', 'title']),
                    image,
                };
            },
        ],
    ]);
})();

export const STATUS_COLOR = {
    DEACTIVATED: '#2A333D',
    DISAPPROVED: '#c72e2c',
    INREVIEW: '#2A333D',
    APPROVED: '#4c87d7',
    PAUSE: '#2A333D',
    PAUSED: '#2A333D',
    RUNNING: '#0054C6',
};

export const DATE_FILTER = [
    '24H',
    'YESTERDAY',
    '7D',
    '14D',
    '30D',
    '1YEAR',
    '2YEARS',
    'LAST_WEEK',
    'LAST_WEEK_SUN_SAT',
    'LAST_MONTH',
    'LAST_QUARTER',
    'WTD',
    'MTD',
    'YTD',
    'THIS_WEEK',
    'THIS_WEEK_SUN_SAT',
    'THIS_MONTH',
    'THIS_QUARTER',
    'THIS_YEAR',
];

const MMP_TRACKERS = ['appsFlyer'];

const ANALYTICS_PREFIX = [
    'ga',
    'af',
    'gss',
    'mailchimp',
    'ga4',
    'branch',
    // 'googleAnalytics',
    // 'googleAnalytics4',
    'mcf',
];

const VALID_CONVERSION_PREFIX = [
    'conversions',
    'cpa',
    'conversionRate',
    'conversionValues',
    'conversionValue',
    'roas',
    'cpaWithTotalSpend',
];

const CONVERSION_TYPES = new Set([
    'googleAnalytics',
    'googleAnalytics4',
    ...ANALYTICS_PREFIX,
    ...MMP_TRACKERS,
    ...VALID_CONVERSION_PREFIX,
]);

export const FIXED_METRIC_VALUE_GROUP = [
    [
        'shopping_product_ad',
        {
            metric: { adName: 'shopping_product_ad' },
            value: ['shopping_product_ad'],
        },
    ],
];

export const METRICS_TYPE = {
    percentage: '%',
    parsedPercentage: '%',
    price: currencyToSymbolMap,
    secondPerVisit: '',
    time: 's',
    timeCount: 'times',
    number: '',
};

export const makeMetricValue = (() => {
    const NULL_SET = new Set([null, undefined, Infinity, NaN]);

    return ({
        data,
        currency,
        isShort = false,
        customFormat = undefined,
        isCurrencyFormat = false,
    }) => {
        data = data || {};

        const { value, type } = data;
        const isKoreanCurrency = currency === 'KRW';
        const METRIC = METRICS_TYPE[type];

        const _d = (format) =>
            formatNumber({ number: value, format, fallback: 0 });

        if (NULL_SET.has(value)) {
            return '-';
        } else if (value == 0) {
            return '0';
        } else if (isKoreanCurrency && isCurrencyFormat) {
            switch (type) {
                case 'price':
                case 'number':
                case 'conversion': {
                    const isPrice = type === 'price';
                    const isNumber = type === 'number';
                    const isThounsandUnit = value < 10000;

                    const enableCompact = !isThounsandUnit || isShort;

                    const formattedNumber = new Intl.NumberFormat('ko-KR', {
                        maximumFractionDigits: 1,
                        ...(enableCompact && { notation: 'compact' }),
                        ...(isPrice
                            ? {
                                  style: 'currency',
                                  currency: 'KRW',
                              }
                            : {
                                  style: 'decimal',
                              }),
                        useGrouping: true,
                    }).format(value);

                    const metricUnit = isNumber ? ` ${METRIC}` : '';
                    return `${formattedNumber}${metricUnit}`;
                }
                case 'fullNumber':
                    return value.toString();
                case 'percentage': {
                    let _value = Number(value);
                    _value = _value * 100;
                    _value = _value.toFixed(2);
                    return `${_value}${METRIC}`;
                }
                case 'parsedPercentage': {
                    const _value = Number(value).toFixed(2);
                    return `${_value}${METRIC}`;
                }
                case 'string':
                    return value;
                default:
                    return `${_d()}${METRIC}`;
            }
        } else {
            switch (type) {
                case 'price': {
                    let format = currency === 'KRW' ? '0,0' : '0,0.[00]';
                    if (customFormat) {
                        format = customFormat;
                    }

                    return isShort
                        ? `${METRIC[currency]} ${_d()}`
                        : `${METRIC[currency]} ${_d(format)}`;
                }
                case 'number':
                    return isShort
                        ? `${_d()}${METRIC}`
                        : `${_d('0,0')}${METRIC}`;
                case 'fullNumber':
                    return value.toString();
                case 'percentage': {
                    let _value = Number(value);
                    _value = _value * 100;
                    _value = _value.toFixed(2);
                    return `${_value}${METRIC}`;
                }
                case 'parsedPercentage': {
                    const _value = Number(value).toFixed(2);
                    return `${_value}${METRIC}`;
                }
                case 'string':
                    return value;
                case 'conversion':
                    return _d();
                default:
                    return `${_d()}${METRIC}`;
            }
        }
    };
})();

const ASSUME_NUMBER_CHANNELS = new Set(['googleSpreadSheet', 'adverity']);

export const changeOrder = (() => {
    const _BASICS = listIncludes(['adrielCampaign', 'campaign']);
    const LEVEL_MAP = {
        all: 2,
    };
    const _level = (level) =>
        !level
            ? -1
            : _BASICS(level)
            ? 10
            : LEVEL_MAP[level]
            ? LEVEL_MAP[level]
            : 9;

    const _from = R.flip(R.prop);
    const _extract = R.cond([
        [Array.isArray, R.identity],
        [
            R.is(Object),
            // budget format is changed { type: String, value: Number } => { daily: Number, lifeTime: Number }
            ({ value, daily, lifeTime }) => value || daily || lifeTime,
        ],
        [R.T, R.identity],
    ]);

    const divideTypes = R.compose(
        R.unnest,
        R.partition(R.compose(R.is(Number), R.prop('value')))
    );

    return (data, isDesc, selected, tOr) => {
        const _levelFunc = memoize(_level);

        const _data = data;

        const indexed = R.indexBy(R.prop('tableId'), _data);
        const orderFunc = isDesc ? R.descend : R.ascend;

        const sortByName = selected === 'name';

        const refineValue = ({ channel, selected = '', value }) => {
            if (
                ASSUME_NUMBER_CHANNELS.has(channel) ||
                (channel && channel.startsWith('internal_'))
            ) {
                const num = numeral(value);

                return num._value === null ? value : num.value();
            }
            switch (selected) {
                case 'status': {
                    return tOr(
                        `NEW_DASHBOARD.ad_status_${R.toLower(value)}`,
                        value
                    );
                }
                default:
                    return value;
            }
        };

        return R.compose(
            R.map(R.compose(_from(indexed), R.prop('tableId'))),
            R.sortBy(R.prop('level')),
            R.sortWith([orderFunc((v) => R.propOr(0, v.by, v))]),
            R.when(R.pathEq([0, 'channel'], 'googleSpreadSheet'), divideTypes),
            R.tap(() => delete _levelFunc.cache),
            R.map((item) => {
                let { id, tableId, cell = {}, level, name, channel } = item;
                const value = _extract(cell[selected]) || '';

                return {
                    name: (name || '-').toString().toLowerCase(),
                    id,
                    tableId,
                    value: refineValue({ channel, selected, value }),
                    level: _levelFunc(level),
                    by: sortByName ? 'name' : 'value',
                    channel,
                };
            })
        )(_data);
    };
})();

/**
 * @return { titles: [Object],  statuses: [Object] ... }
 */
export const parseTagsToParam = ({ filters = [] }) => {
    const parsedFilters = {};

    for (let filter of filters) {
        let { type, filterBy } = filter;

        const isBreakdownLabel = type === 'breakdownLabels';

        const value =
            typeof filterBy === 'object'
                ? isBreakdownLabel
                    ? filterBy.labelId
                    : filterBy.value
                : filterBy;

        if (value) {
            if (isBreakdownLabel) {
                filterBy = R.pick(['dimensionId', 'labelId'], filterBy);
            }

            if (Object.prototype.hasOwnProperty.call(parsedFilters, type)) {
                parsedFilters[type].push(filterBy);
            } else {
                parsedFilters[type] = [filterBy];
            }
        }
        // }
    }
    return parsedFilters;
};

export const FLATTEN_LIST = ({
    data = [],
    isExpanded = R.T,
    depth = 0,
    parentId,
    breakFunc = R.F,
    isCustom = true,
    sortFunc = R.identity,
} = {}) => {
    if (data.length === 0) {
        return [];
    }
    let result = [];

    data.forEach((datum) => {
        const { children = [], campaign = {} } = datum;

        const _isCustom =
            datum.isCustom === false || campaign.isCustom === false
                ? false
                : isCustom;

        result.push(
            Object.assign({}, datum, {
                depth,
                parentId,
                isCustom: _isCustom,
                id: datum.id || datum.tableId || generateId(),
            })
        );

        if (!breakFunc(datum) && (isExpanded(datum) || datum.expanded)) {
            result.push(
                ...FLATTEN_LIST({
                    data: sortFunc(children),
                    isExpanded,
                    depth: R.inc(depth),
                    parentId: datum.id,
                    isCustom: _isCustom,
                    sortFunc,
                })
            );
        }
    });

    return result;
};

export function getJoinedClassList(e) {
    const target = e.toElement || e.relatedTarget || {};
    return Array.from(target.classList || []).join('');
}

export const GET_MERGE_RIGHT_LIST_ITEM = ({ itemType, isCustom }) => {
    return R.compose(
        R.mergeDeepRight({
            itemType,
            isCustom,
            options: {},
        }),
        R.defaultTo({})
    );
};

/**
 * Splits cells based on delimiters __ and :, ignoring colons inside parentheses or URLs.
 */
const splitCell = (str) => {
    // __를 :로 변환
    str = str.replace(/__/g, ':');

    let result = [];
    let buffer = '';
    let bracketCount = 0;
    let i = 0;

    while (i < str.length) {
        if (str[i] === '(') bracketCount++;
        if (str[i] === ')') bracketCount = Math.max(0, bracketCount - 1);

        // ignore : inside brackets
        if (str[i] === ':' && bracketCount === 0) {
            const next = str.slice(i + 1);

            // ignore : inside url
            if (next.match(/^(\/\/|\s)/)) {
                buffer += str[i];
            } else {
                result.push(buffer);
                buffer = '';
            }
        } else {
            buffer += str[i];
        }

        i++;
    }

    if (buffer) result.push(buffer);
    return result;
};

/**
 * Extracts cell information based on delimiters __ and :.
 * Order: metric, tracker, eventName, etc.
 */
export const getConversionCellInfo = memoize(function (cellName = '') {
    if (!R.is(String, cellName)) {
        cellName = String(cellName);
    }

    const [metric, tracker, eventName, etc] = splitCell(cellName);

    return {
        metric,
        tracker,
        eventName,
        etc,
    };
});

export const formatCreative = (creativeType, creative = {}) => {
    let _creative = {
        ...(creative.params || {}),
        ...creative,
    };

    if (
        listIncludes(['ExpandedTextAd'], creativeType) &&
        R.has('titles', _creative)
    ) {
        const _pathOr = R.pathOr('');

        _creative = {
            ...R.applySpec({
                description: _pathOr(['descriptions', 0]),
                description2: _pathOr(['descriptions', 1]),
                title1: _pathOr(['titles', 0]),
                title2: _pathOr(['titles', 1]),
                title3: _pathOr(['titles', 2]),
                url: _pathOr(['url']),
            })(_creative),
            ..._creative,
        };
    }

    return _creative || {};
};

export const PREVIEW_AD = testerFromArray([
    'google_ExpandedTextAd',
    // 'google_EXPANDED_DYNAMIC_SEARCH_AD',
    'microsoft_ExpandedText',
    'microsoft_ResponsiveSearch',
    'naver_BRAND_SEARCH_AD',
    'naver_TEXT_45',
    'naver_SHOPPING_PRODUCT_AD',
]);

export const PREVIEW_PROPS = R.assocPath(['attrs', 'value', 'creative'], R.__, {
    attrs: {
        value: {
            meta: {
                isEditing: false,
            },
        },
    },
});

export const permittedActions = (() => {
    const {
        permittedActions: actions,
        disableReasons: reasons,
        actionTypes: types,
    } = editableElement;

    const indexTypeAndFreeze = R.compose(
        Object.freeze,
        R.indexBy(R.prop('type'))
    );

    const DISABLED_DEFAULT = R.compose(
        indexTypeAndFreeze,
        R.map(
            R.assoc('type', R.__, {
                isAvailable: false,
                reason: reasons.LVL_NOT_AVAILABLE,
            })
        ),
        Object.values
    )(types);

    return ({ item }) => {
        const { level, id, rowId } = item;

        try {
            return indexTypeAndFreeze(
                actions({
                    elementId: rowId || id,
                    item,
                    level,
                })
            );
        } catch (err) {
            return DISABLED_DEFAULT;
        }
    };
})();

const isCustomBreakdown = R.is(Number);
/**
 * Keeps custom breakdowns in `rows` order even when removed from `dimensions`.
 * If no match exists, returns `{ id }` as a placeholder.
 */
export const makeDimension = (dimensions, rows) => {
    if (!dimensions || !isValueExist(rows)) {
        return [];
    } else {
        const dById = R.indexBy(R.prop('id'), dimensions);
        return R.compose(
            R.map((id) => {
                if (isCustomBreakdown(id)) {
                    return dById[id] ?? { id };
                }

                return id;
            })
        )(rows);
    }
};

// mutate origin data in place
export const refineGridData = (() => {
    const GET_MERGER = (itemType = 'customItem') =>
        GET_MERGE_RIGHT_LIST_ITEM({
            itemType,
            isCustom: true,
        });

    const CAMPAIGN_MERGER = GET_MERGER('campaign');

    const _map = {
        customDimension: GET_MERGER(),
        adrielCampaign: R.compose(R.assoc('isCustom', false), CAMPAIGN_MERGER),
        campaign: CAMPAIGN_MERGER,
        adSet: CAMPAIGN_MERGER,
        ad: R.compose(
            (item) =>
                R.assoc(
                    'creativeType',
                    R.path(['creative', 'type'], item),
                    item
                ),
            GET_MERGER('preview')
        ),
        creativeImage: GET_MERGER('preview'),
        channel: GET_MERGER('channel'),
        keyword: GET_MERGER('badge'),
        age: GET_MERGER('icon'),
        location: GET_MERGER('icon'),
        gender: GET_MERGER('icon'),
        deviceTarget: GET_MERGER('icon'),
        subChannel: GET_MERGER('channel'),
        all: GET_MERGER('icon'),
        imageHash: GET_MERGER('preview'),
    };

    const _get = (data) => {
        let merger = _map[data.level];

        // if (!merger) {
        //     console.log(data.level);
        // }

        if (!data.id) {
            data.id = generateId();
        }

        return (merger || _map['customDimension'])(data);
    };

    return (data) => {
        if (!data || !data.length) {
            return [];
        }

        for (let d of data) {
            Object.assign(d, _get(d));
            refineGridData(d.children);
        }

        return data;
    };
})();

export const isConversionCategory = ({ category, name = '' }) =>
    category === 'conversion' &&
    name.split(':').length > 1 &&
    R.any(R.startsWith(R.__, name), VALID_CONVERSION_PREFIX);

export const DATE_GROUP_ROWS = [
    'day',
    'week',
    'weekSunSat',
    'nikeWeekNumber', // for nike
    'weekDate',
    'month',
    'autoTime',
    'year',
    'quarter',
];

export const DATE_GROUP_ROWS_WITHOUT_WEEKDATE = [
    'day',
    'week',
    'weekSunSat',
    'month',
    'autoTime',
];

export const AUDIENCE_ROWS = [
    'age',
    'gender',
    'location',
    'deviceTarget',
    'os',
    'hourOfDay',
    'country',
];

export const DYNAMIC_CREATIVE_ROWS = [
    'creativeTitle',
    'creativeDescription',
    'creativeImage',
];

export const LINKEDIN_AUDIENCE_ROWS = [
    'industry',
    'companySize',
    'seniority',
    'jobTitle',
    'jobFunction',
];

export const BASIC_ROW_LIST = [
    'all',
    'channel',
    'adAccount',
    'campaign',
    'adSet',
    'ad',
    'objectives',
    'status',
    'keyword',
    'subChannel',
    'creativeWord',
    'platformPlacement',
    'colorClustering',
    'emojiAnalysis',
    'imageHash',
    'keywordText',
    'campaignName',
    'adSetName',
    'adName',
    'postId',
    'googleAdGroupAdAsset',
    'dmaRegion',
    // 'placement',
    ...DATE_GROUP_ROWS,
    ...AUDIENCE_ROWS,
    ...DYNAMIC_CREATIVE_ROWS,
    ...LINKEDIN_AUDIENCE_ROWS,
];

export const setWidgetInsideView = ({ view, widget } = {}) => {
    const widgets = R.pathOr([], ['config', 'widgets'], view);
    const idx = widgets.findIndex(R.propEq('id', widget.id));

    return R.assocPath(['config', 'widgets', idx], widget, view);
};

export const STORAGE_TYPES = {
    STORE: '[STORAGE] STORE',
    SERVER: '[STORAGE] SERVER',
};
export const COMPARISON_DATE_FILTER = {
    previous: 'previous',
    month: 'month',
    quarter: 'quarter',
    year: 'year',
    pick: 'pick',
};
export const calcComparisonDate = ({
    status,
    comparedDate,
    dateFilterStatus,
}) => {
    const { previous, month, quarter, year } = COMPARISON_DATE_FILTER;

    const { from, to } = comparedDate;

    let calcFrom, calcTo;

    switch (status) {
        case previous: {
            const gapDays = moment(to).diff(moment(from), 'days') + 1;

            if (R.includes(dateFilterStatus, ['MTD', 'WTD', 'YTD'])) {
                const unit = DATEFILTER_STATUS_VALUE_MAP[dateFilterStatus];
                calcFrom = moment(from).subtract(1, unit).format('YYYY-MM-DD');
                calcTo = moment(to).subtract(1, unit).format('YYYY-MM-DD');
            } else if (gapDays === 0) {
                /**
                 * @note exception for today vs previous case
                 */
                calcFrom = moment(from).subtract(1, 'day').format('YYYY-MM-DD');
                calcTo = moment(to).subtract(1, 'day').format('YYYY-MM-DD');
            } else {
                calcFrom = moment(from)
                    .subtract(gapDays, 'day')
                    .format('YYYY-MM-DD');
                calcTo = moment(to)
                    .subtract(gapDays, 'day')
                    .format('YYYY-MM-DD');
            }

            break;
        }
        case month: {
            calcFrom = moment(from).subtract(1, 'month').format('YYYY-MM-DD');
            calcTo = moment(to).subtract(1, 'month').format('YYYY-MM-DD');
            break;
        }
        case quarter: {
            calcFrom = moment(from).subtract(3, 'month').format('YYYY-MM-DD');
            calcTo = moment(to).subtract(3, 'month').format('YYYY-MM-DD');
            break;
        }
        case year: {
            calcFrom = moment(from).subtract(1, 'year').format('YYYY-MM-DD');
            calcTo = moment(to).subtract(1, 'year').format('YYYY-MM-DD');
            break;
        }
    }

    return { from: calcFrom, to: calcTo };
};

export const getComparisonDatePeriod = ({
    dateFilter,
    comparisonDateFilter,
}) => {
    const { from, to } = dateFilter;
    const { from: cfrom, to: cto } = comparisonDateFilter;
    const { previous, month, year, quarter, pick } = COMPARISON_DATE_FILTER;

    const gapDays = moment(to).diff(moment(from), 'days') + 1;

    const calcPrevious = (days) =>
        moment(days).add(gapDays, 'day').format('YYYY-MM-DD');

    const calcMonth = (month) =>
        moment(month).add(1, 'month').format('YYYY-MM-DD');

    const calcQuarter = (month) =>
        moment(month).add(3, 'month').format('YYYY-MM-DD');

    const calcYear = (year) => moment(year).add(1, 'year').format('YYYY-MM-DD');

    if (
        moment(calcPrevious(cfrom)).isSame(from) &&
        moment(calcPrevious(cto)).isSame(to)
    ) {
        return previous;
    } else if (
        moment(calcMonth(cfrom)).isSame(from) &&
        moment(calcMonth(cto)).isSame(to)
    ) {
        return month;
    } else if (
        moment(calcQuarter(cfrom)).isSame(from) &&
        moment(calcQuarter(cto)).isSame(to)
    ) {
        return quarter;
    } else if (
        moment(calcYear(cfrom)).isSame(from) &&
        moment(calcYear(cto)).isSame(to)
    ) {
        return year;
    } else {
        return pick;
    }
};

const getMonthText = (month) => {
    const idx = Math.max(0, month - 1);
    return MONTH_NAMES.en[idx].slice(0, 3);
};

export const renderPeriod = function (date = {}) {
    const { from = undefined, to = undefined } = date;
    if (!from && !to) {
        return this.$t('NEW_DASHBOARD.filter_date_pick');
    }

    const startDateText = () => {
        if (!from) {
            return;
        }

        return commDateFormat(
            from || pastNDays(30),
            this.$t('COMMON.full_date_format'),
            this.language
        );
    };

    const endDateText = () => {
        if (!to) {
            return;
        }
        return commDateFormat(
            to || pastNDays(),
            this.$t('COMMON.full_date_format'),
            this.language
        );
    };

    const dateTranslator = (date) =>
        this.$t('COMMON.date_only_format', {
            date,
        });

    const defaultTo = R.defaultTo('-');

    if (!from && !to) return this.$t('NEW_DASHBOARD.filter_date_pick');

    if (!from) return defaultTo(endDateText());

    if (!to) return defaultTo(startDateText());

    if (from === to) return startDateText();

    const [fromY, fromM, fromD] = from?.split('-').map((v) => +v);
    const [toY, toM, toD] = to?.split('-').map((v) => +v);

    //  년, 월 중복
    if (fromY === toY && fromM === toM) {
        const enText = `${dateTranslator(fromD)} - ${endDateText()}`;
        const krText = `${startDateText()} - ${dateTranslator(toD)}`;

        return this.language !== 'ko' ? enText : krText;
    }

    // 년 중복
    if (fromY === toY) {
        const fromMText = getMonthText(fromM);
        const enText = `${fromD} ${fromMText} - ${endDateText()}`;
        const krText = `${startDateText()} - ${toM}월 ${toD}일`;

        return this.language !== 'ko' ? enText : krText;
    }

    return `${startDateText()} - ${endDateText()}`;
};

export const isFolder = R.compose(
    R.test(/folder/gi),
    R.propOr('', 'type'),
    R.when(R.is(String), R.objOf('type'))
);

export const isView = R.complement(isFolder);

export const isMenu = R.allPass([
    isFolder,
    R.compose(
        R.test(/menu/gi),
        R.propOr('', 'subtype'),
        R.when(R.is(String), R.objOf('subtype'))
    ),
]);

export const checkIsHidden = R.propEq('subtype', 'bo');
export const checkIsGeneral = R.propEq('subtype', 'general');
export const checkIsTeamspace = R.propEq('type', 'teamspace');
export const checkIsFolder = R.propEq('type', 'folder');
export const checkIsView = R.propEq('type', 'view');

export function FLATTEN_VIEWS(data = [], path = []) {
    if (typeof data === 'object') {
        data = guaranteeArray(data);
    }

    let result = [];

    data.filter(isValueExist).forEach((datum, idx) => {
        const { children = [] } = datum;

        const cPath = [...path, idx];
        result.push({ ...datum, path: cPath });

        result.push(...FLATTEN_VIEWS(children, cPath));
    });

    return result;
}

export function VIEW_PATH_MAP({ data = [], path = [], map = {} }) {
    data.forEach((datum, idx) => {
        const { children = [] } = datum;

        const cPath = [...path, idx];
        map[datum.id] = cPath;
        VIEW_PATH_MAP({ data: children, path: cPath, map });
    });

    return map;
}

export function getParentView({ views = [], view = {}, pathMap } = {}) {
    pathMap = isValueExist(pathMap) ? pathMap : VIEW_PATH_MAP({ data: views });

    let path = pathMap[view.id] || [];
    path = R.intersperse('children', path);
    path = R.dropLast(2, path);

    return path.length < 1 ? null : R.pathOr(null, path, views);
}

const formatter = R.curryN(3, commDateFormat)(R.__, 'YYYY-MM-DD');
const YEAR = 365;

export const LAST_WEEK_DATES = (function () {
    return [
        moment(new Date()).subtract(1, 'weeks').startOf('isoWeek'),
        moment(new Date()).subtract(1, 'weeks').endOf('isoWeek'),
    ].map(formatter);
})();

export const LAST_WEEK_SUN_SAT_DATES = (function () {
    return [
        moment(new Date()).subtract(1, 'weeks').startOf('week'),
        moment(new Date()).subtract(1, 'weeks').endOf('week'),
    ].map(formatter);
})();

export const LAST_MONTH_DATES = (function () {
    return [
        moment(new Date()).subtract(1, 'months').startOf('month'),
        moment(new Date()).subtract(1, 'months').endOf('month'),
    ].map(formatter);
})();

export const LAST_QUATER_DATES = (function () {
    return [
        moment().subtract(3, 'months').startOf('quarter'),
        moment().subtract(3, 'months').endOf('quarter'),
    ].map(formatter);
})();

export const THIS_YEAR_DATES = (function () {
    return [moment().startOf('year'), moment().endOf('year')].map(formatter);
})();

export const THIS_QUARTER_DATES = (function () {
    return [moment().startOf('quarter'), moment().endOf('quarter')].map(
        formatter
    );
})();

export const THIS_MONTH_DATES = (function () {
    return [moment().startOf('month'), moment().endOf('month')].map(formatter);
})();

export const THIS_WEEK_DATES = (function () {
    return [moment().startOf('isoWeek'), moment().endOf('isoWeek')].map(
        formatter
    );
})();

export const THIS_WEEK_SUN_SAT_DATES = (function () {
    return [moment().startOf('week'), moment().endOf('week')].map(formatter);
})();

export const DATES = Object.freeze({
    '24H': [new Date(), new Date()].map(formatter),
    YESTERDAY: [pastNDays(1), pastNDays(1)].map(formatter),
    '7D': [pastNDays(6), new Date()].map(formatter),
    '14D': [pastNDays(13), new Date()].map(formatter),
    '30D': [pastNDays(29), new Date()].map(formatter),
    '1YEAR': [pastNDays(YEAR - 1), new Date()].map(formatter),
    '2YEARS': [pastNDays(YEAR * 2 - 1), new Date()].map(formatter),
    LAST_WEEK: LAST_WEEK_DATES,
    LAST_WEEK_SUN_SAT: LAST_WEEK_SUN_SAT_DATES,
    LAST_MONTH: LAST_MONTH_DATES,
    LAST_QUARTER: LAST_QUATER_DATES,
    MTD: [moment().startOf('month'), new Date()].map(formatter),
    WTD: [moment().startOf('isoWeek'), new Date()].map(formatter),
    THIS_YEAR: THIS_YEAR_DATES,
    THIS_QUARTER: THIS_QUARTER_DATES,
    THIS_MONTH: THIS_MONTH_DATES,
    THIS_WEEK: THIS_WEEK_DATES,
    THIS_WEEK_SUN_SAT: THIS_WEEK_SUN_SAT_DATES,
});

export const DATEFILTER_STATUS_VALUE_MAP = {
    YTD: 'year',
    MTD: 'month',
    WTD: 'isoWeek',
};

export const DATE_FILTER_MAP = {
    '24H': '24H',
    YESTERDAY: 'YESTERDAY',
    '7D': '7D',
    '14D': '14D',
    '30D': '30D',
    '1YEAR': '1YEAR',
    '2YEARS': '2YEARS',
    LAST_WEEK: 'LAST_WEEK',
    LAST_WEEK_SUN_SAT: 'LAST_WEEK_SUN_SAT',
    LAST_MONTH: 'LAST_MONTH',
    LAST_QUARTER: 'LAST_QUARTER',
    WTD: 'WTD',
    MTD: 'MTD',
    YTD: 'YTD',
    THIS_YEAR: 'THIS_YEAR',
    THIS_QUARTER: 'THIS_QUARTER',
    THIS_MONTH: 'THIS_MONTH',
    THIS_WEEK: 'THIS_WEEK',
    THIS_WEEK_SUN_SAT: 'THIS_WEEK_SUN_SAT',
};

export function convertRelativeDate(relativeValue) {
    let { value, unit } = relativeValue;
    let to = new Date();
    let from;

    if (value === DATE_FILTER_MAP.LAST_WEEK) {
        const [from, to] = LAST_WEEK_DATES;
        return { from, to };
    }

    if (value === DATE_FILTER_MAP.LAST_WEEK_SUN_SAT) {
        const [from, to] = LAST_WEEK_SUN_SAT_DATES;
        return { from, to };
    }

    if (value === DATE_FILTER_MAP.LAST_MONTH) {
        const [from, to] = LAST_MONTH_DATES;
        return { from, to };
    }

    if (value === DATE_FILTER_MAP.LAST_QUARTER) {
        const [from, to] = LAST_QUATER_DATES;
        return { from, to };
    }

    if (value === DATE_FILTER_MAP.THIS_YEAR) {
        const [from, to] = THIS_YEAR_DATES;
        return { from, to };
    }

    if (value === DATE_FILTER_MAP.THIS_QUARTER) {
        const [from, to] = THIS_QUARTER_DATES;
        return { from, to };
    }

    if (value === DATE_FILTER_MAP.THIS_MONTH) {
        const [from, to] = THIS_MONTH_DATES;
        return { from, to };
    }

    if (value === DATE_FILTER_MAP.THIS_WEEK) {
        const [from, to] = THIS_WEEK_DATES;
        return { from, to };
    }

    if (value === DATE_FILTER_MAP.THIS_WEEK_SUN_SAT) {
        const [from, to] = THIS_WEEK_SUN_SAT_DATES;
        return { from, to };
    }

    if (R.includes(value, ['YTD', 'MTD', 'WTD'])) {
        const unit = DATEFILTER_STATUS_VALUE_MAP[value];
        from = moment().startOf(unit);
    } else {
        if (unit === 'Y') {
            value = value * YEAR - 1;
        } else if (unit === 'M') {
            value = value * 30 - 1;
        } else if (unit === 'D' && value === 1) {
            to = pastNDays(1);
        } else if (unit === 'D' && value === 0) {
            to = pastNDays(0);
            value = 0;
        } else if (unit === 'D' && value > 0) {
            value = value - 1;
        }

        from = pastNDays(value);
    }

    return {
        to: formatter(to, undefined),
        from: formatter(from, undefined),
    };
}

export const isAdvancedDateFilter = (filter) => {
    return (
        isValueExist(filter) &&
        filter.type === 'advanced' &&
        isValueExist(filter.values?.advanced)
    );
};

export function getDateFilterStatus({ date }) {
    let { from, to } = date;

    if (!(from && to) || !date) {
        return '30D';
    }

    const EQ = R.equals([from, to]);

    let status = 'pick';

    DATE_FILTER.some((dateUnit) => {
        if (EQ(DATES[dateUnit])) {
            status = dateUnit;
            return true;
        }
    });

    return status;
}

export function getDateFilter({
    date,
    todayExcluded = false,
    dateFilterValue,
    savedValue = {},
}) {
    if (!isValueExist(date)) {
        const [defaultFrom, defaultTo] = getDefaultDate();
        date = { from: defaultFrom, to: defaultTo };
    }

    const isMTD = dateFilterValue === 'MTD';
    const isWTD = dateFilterValue === 'WTD';
    const isYTD = dateFilterValue === 'YTD';
    const isValueThisMonth = dateFilterValue === 'THIS_MONTH';
    const isValueStatusKind = isMTD || isWTD || isYTD;

    let { from, to } = date;

    const isToAndFromSame = to === from;

    if (isToAndFromSame && !isValueStatusKind) {
        todayExcluded = false;
    }

    const now = new Date();
    const today = moment(now).format('YYYY-MM-DD');
    const isTodayIncluded = moment(today).isSame(to);
    const isEndOfMonth = moment(today).isSame(
        moment(today).endOf('month'),
        'day'
    );

    if (todayExcluded && isTodayIncluded) {
        if (isValueStatusKind) {
            if (isToAndFromSame) {
                const firstDayOfMonth = moment().format('YYYY-MM-01');
                const isMTDAndFirstDayOfMonth =
                    isMTD && firstDayOfMonth === today;
                const isYTDAndFirstDayOfMonth =
                    isYTD && firstDayOfMonth === today;
                const isWTDAndIsMonday = isWTD && moment().weekday() === 1; // Monday

                if (isMTDAndFirstDayOfMonth) {
                    /**
                     * MTD: 이번달의 첫째날, todayExcluded가 체크되어있는 상태에서
                     * 이날 하루를 조회하려고 한다면 "지난 달의 Full date"을 Return
                     * */
                    const lastMonth = moment().subtract(1, 'months');

                    from = lastMonth.startOf('month').format('YYYY-MM-DD');
                    to = lastMonth.endOf('month').format('YYYY-MM-DD');
                } else if (isWTDAndIsMonday) {
                    /**
                     * WTD: 이번주의 월요일, todayExcluded가 체크되어있는 상태에서
                     * 이날 하루를 조회하려고 한다면 "지난 주의 Full week"을 Return
                     * */
                    const lastWeek = moment().subtract(1, 'weeks');

                    from = lastWeek.startOf('isoWeek').format('YYYY-MM-DD');
                    to = lastWeek.endOf('isoWeek').format('YYYY-MM-DD');
                } else if (isYTDAndFirstDayOfMonth) {
                    const lastYear = moment().subtract(1, 'year');

                    from = lastYear.startOf('year').format('YYYY-MM-DD');
                    to = lastYear.endOf('year').format('YYYY-MM-DD');
                }
            } else {
                // MTD, WTD 일때, excludeToday의 to는 제외
                to = moment(to).subtract(1, 'days').format('YYYY-MM-DD');
            }
        } else {
            to = moment(to).subtract(1, 'days').format('YYYY-MM-DD');
            if (!(isEndOfMonth && isValueThisMonth)) {
                from = moment(from).subtract(1, 'days').format('YYYY-MM-DD');
            }
        }
    }

    const _propOr = R.propOr('', 'value');

    const isToDateValue = R.compose(
        R.includes(R.__, ['MTD', 'WTD', 'YTD']),
        _propOr
    )(savedValue);

    const dateFilterStatus =
        isToDateValue || isValueThisMonth
            ? _propOr(savedValue)
            : getDateFilterStatus({ date });

    return {
        dateFilter: { from, to },
        dateFilterStatus,
    };
}

export function getComparisonDateFilter({
    date = {},
    comparedDate,
    period = '',
    dateFilterStatus,
}) {
    // When it's not comparison mode: default
    let comparisonDateFilter = {};
    let comparisonDateStatus = 'previous';

    // Comparison mode 'relative'
    if (!isValueExist(date) && isValueExist(period)) {
        comparisonDateFilter = calcComparisonDate({
            status: period,
            comparedDate,
            dateFilterStatus,
        });

        comparisonDateStatus = period;
    } else if (isValueExist(date)) {
        // Comparison mode 'pick' or URL query
        comparisonDateFilter = date;
        comparisonDateStatus = getComparisonDatePeriod({
            dateFilter: comparedDate,
            comparisonDateFilter: date,
        });
    }

    return {
        comparisonDateFilter,
        comparisonDateStatus,
    };
}

const MAX_ALLOWED_ADVANCED_VALUE_BY_UNIT = {
    D: 36500, // ~100 years in days
    W_SUN: 5200, // ~100 years in weeks
    W_MON: 5200,
    M: 1200, // 100 years in months
    Q: 400, // 100 years in quarters
    Y: 100, // 100 years
};

export const parseAdvancedRelativeValueToDateString = (
    advancedRelativeValue,
    isEndDate = false,
    todayString
) => {
    /* eslint-disable no-unexpected-multiline */
    if (
        typeof advancedRelativeValue !== 'object' ||
        !('unit' in advancedRelativeValue)
    ) {
        return advancedRelativeValue;
    }

    const { unit, value, isNext = false } = advancedRelativeValue;

    if (
        typeof value !== 'number' ||
        isNaN(value) ||
        value < 0 ||
        value > (MAX_ALLOWED_ADVANCED_VALUE_BY_UNIT[unit] || 0)
    ) {
        return 'Invalid Date';
    }

    const today = moment(todayString).isValid()
        ? moment(todayString)
        : moment();
    const direction = isNext ? 1 : -1;
    const _value = value * direction;

    switch (unit) {
        case 'D':
            today.add(_value, 'days');
            break;
        case 'W_SUN':
            today.add(_value, 'weeks')[isEndDate ? 'endOf' : 'startOf']('week');
            break;
        case 'W_MON':
            today
                .add(_value, 'weeks')
                [isEndDate ? 'endOf' : 'startOf']('isoWeek');
            break;
        case 'M':
            today
                .add(_value, 'months')
                [isEndDate ? 'endOf' : 'startOf']('month');
            break;
        case 'Q':
            today
                .add(_value, 'quarters')
                [isEndDate ? 'endOf' : 'startOf']('quarter');
            break;
        case 'Y':
            today.add(_value, 'years')[isEndDate ? 'endOf' : 'startOf']('year');
            break;
        default:
            break;
    }

    if (today.isValid()) {
        return today.format('YYYY-MM-DD');
    }

    return 'Invalid Date';
};

export function getAdvancedDateFilter({ dateFilter }) {
    if (
        !isValueExist(dateFilter?.values?.advanced) ||
        dateFilter?.type !== 'advanced'
    ) {
        return {
            from: moment().format('YYYY-MM-DD'),
            to: moment().format('YYYY-MM-DD'),
        };
    }

    const { from, to } = dateFilter.values.advanced;

    return {
        from:
            typeof from === 'string'
                ? from
                : parseAdvancedRelativeValueToDateString(from),
        to:
            typeof to === 'string'
                ? to
                : parseAdvancedRelativeValueToDateString(to, true),
    };
}

export const updateList = R.curryN(2, (mapper = R.identity, list = []) => {
    if (R.isEmpty(list)) {
        return list;
    } else {
        return R.compose(
            R.map(
                R.when(
                    R.propSatisfies(Array.isArray, 'children'),
                    R.over(R.lensProp('children'), updateList(mapper))
                )
            ),
            R.map(mapper)
        )(list);
    }
});

export const leftClamp = R.clamp(232, 500);

export const HIDDEN_ROW_IDS = new Set(['adrielCampaign', 'objective']);

export const whenFolder = R.when(isFolder);

export const isCustomMetric = R.compose(
    R.test(/^custom-metric:\d+/),
    R.defaultTo(''),
    R.when(R.is(Object), R.prop('name'))
);

export const refineSourceName = function (v) {
    const {
        name,
        channelId,
        channel,
        title,
        displayName,
        architecture: { name: archName } = {},
    } = v;

    const refinedName = name || archName;
    switch (channel) {
        case 'googleSpreadSheet': {
            return displayName || `${title} | ${refinedName}`;
        }
        case 'googleAnalytics4': {
            return refinedName;
        }
        default: {
            return refinedName ? `${refinedName} | ${channelId}` : channelId;
        }
    }
};

export const refineSourceNameWithDeprecated = function (v) {
    const { isDeprecated } = v;

    const sourceName = refineSourceName(v);

    return isDeprecated
        ? `${this.translateOr(
              'NEW_DASHBOARD.source_setting_option.tag.deprecated'
          )} ${sourceName}`
        : sourceName;
};

export const ERROR_TYPE = {
    ERROR: 'ERROR',
    WARNING: 'WARNING',
    INFO: 'INFO',
};
const { ERROR, WARNING, INFO } = ERROR_TYPE;

export const SERVER_UNRESPONSIVE = 'SERVER_UNRESPONSIVE'; //
export const USER_DELETED = 'USER_DELETED'; //
export const EXTERNAL_SERVICE_UNAVAILABLE = 'EXTERNAL_SERVICE_UNAVAILABLE';
export const EXTERNAL_SERVICE_UNAVAILABLE_CAUGHT =
    'EXTERNAL_SERVICE_UNAVAILABLE_CAUGHT'; //
export const EXTERNAL_SERVICE_PERMISSION_MISSING =
    'EXTERNAL_SERVICE_PERMISSION_MISSING'; //
export const FEATURE_UNAVAILABLE = 'FEATURE_UNAVAILABLE'; //
export const NEED_USER_ACTION = 'NEED_USER_ACTION'; //
export const RETRY_LATTER = 'RETRY_LATTER';
export const SHOULD_CHANGE_PARAMS = 'SHOULD_CHANGE_PARAMS'; //
export const FROM_DATE_TOO_OLD = 'FROM_DATE_TOO_OLD';
export const INVALID_DIMENSIONS_METRICS_COMBINATION =
    'INVALID_DIMENSIONS_METRICS_COMBINATION';
export const INVALID_QUERY = 'INVALID_QUERY';
export const NO_METRICS_AND_DIMENSIONS = 'NO_METRICS_AND_DIMENSIONS';
export const METRICS_DIMENSIONS_NO_PREFIX = 'METRICS_DIMENSIONS_NO_PREFIX'; //
export const RATE_LIMIT = 'RATE_LIMIT';
export const UNKNOWN = 'UNKNOWN'; //
export const FILTER_UNSUPPORTED = 'FILTER_UNSUPPORTED'; //
export const METRICS_NOT_AVAILABLE = 'METRICS_NOT_AVAILABLE'; //
export const GSS_INVALID_SHEET_NAME = 'GSS_INVALID_SHEET_NAME';
export const TOO_MUCH_DATA_REQUESTED = 'TOO_MUCH_DATA_REQUESTED';
export const AIRTABLE_UNKNOWN_FIELD_NAME = 'AIRTABLE_UNKNOWN_FIELD_NAME'; //
export const MISSING_EXTERNAL_PERMISSIONS = 'MISSING_EXTERNAL_PERMISSIONS'; //
export const INVALID_DATA_SOURCE = 'INVALID_DATA_SOURCE';
export const EXTERNAL_SERVICE_INTERNAL_ERROR =
    'EXTERNAL_SERVICE_INTERNAL_ERROR';

export const DATE_SETTING_REQUIRED = 'DATE_SETTING_REQUIRED';
export const DEFAULT_ERROR = 'DEFAULT_ERROR';
export const CONNECTION_LOST = 'CONNECTION_LOST';
export const CONNECTION_LOST_WITHIN_7_DAYS = 'CONNECTION_LOST_WITHIN_7_DAYS';
export const INTERNAL_ERROR = 'INTERNAL_ERROR';
export const TOO_MANY_ADS_ON_GA = 'TOO_MANY_ADS_ON_GA';
export const TOKEN_ISSUE = 'TOKEN_ISSUE';
export const INVALID_CUSTOM_BREAKDOWN = 'INVALID_CUSTOM_BREAKDOWN'; //
export const CUSTOM_BREAKDOWN_REGEX_NO_MATCH =
    'CUSTOM_BREAKDOWN_REGEX_NO_MATCH';
export const DATE_BREAKDOWN_UNSUPPORTED = 'DATE_BREAKDOWN_UNSUPPORTED'; //
export const DATE_RANGE_TOO_LONG = 'DATE_RANGE_TOO_LONG';
export const INTERNAL_SERVER_ERROR = 'INTERNAL_SERVER_ERROR';
export const TOO_MANY_DATA_SOURCES = 'TOO_MANY_DATA_SOURCES';
export const INVALID_DATE_FORMAT = 'INVALID_DATE_FORMAT';
export const INVALID_URL = 'INVALID_URL';
export const MISSING_DATABASE_COLUMNS = 'MISSING_DATABASE_COLUMNS';
export const INVALID_BREAKDOWN_FOR_CHART = 'INVALID_BREAKDOWN_FOR_CHART';
export const TOO_MUCH_DATA_REQUESTED_INCOMPLETE =
    'TOO_MUCH_DATA_REQUESTED_INCOMPLETE';
export const TOO_MUCH_DATA_REQUESTED_1_YEAR = 'TOO_MUCH_DATA_REQUESTED_1_YEAR';
export const INVALID_LEVEL_DIMENSION = 'INVALID_LEVEL_DIMENSION';
export const CANT_SUM_METRIC = 'CANT_SUM_METRIC';
export const GA_SAMPLED_DATA = 'GA_SAMPLED_DATA';
export const GA4_NO_ALPHA_CHARACTERS = 'GA4_NO_ALPHA_CHARACTERS';
export const SUM_TOO_BIG_TO_AGGREGATE = 'SUM_TOO_BIG_TO_AGGREGATE';
export const DATA_PRUNED = 'DATA_PRUNED';
export const INACTIVE_USER_DATA_REMOVED = 'INACTIVE_USER_DATA_REMOVED';
export const BREAKDOWN_NOT_AVAILABLE = 'BREAKDOWN_NOT_AVAILABLE';
export const RISK_DUPLICATED_DATA = 'RISK_DUPLICATED_DATA';
export const REQUIRE_LEVEL_BREAKDOWN = 'REQUIRE_LEVEL_BREAKDOWN';
export const CONNECT_SERVER_UNRESPONSIVE = 'CONNECT_SERVER_UNRESPONSIVE';
export const REPORT_DATA_IS_STILL_FETCHING = 'REPORT_DATA_IS_STILL_FETCHING';
export const INACTIVE_USER_DATA_NOT_AVAILABLE =
    'INACTIVE_USER_DATA_NOT_AVAILABLE';
export const TOO_MUCH_API_CALLS_REQUESTED = 'TOO_MUCH_API_CALLS_REQUESTED';
export const INVALID_DIMENSIONS_COMBINATION = 'INVALID_DIMENSIONS_COMBINATION';
export const EXCESSIVE_METRICS_SELECTED = 'EXCESSIVE_METRICS_SELECTED';
export const CREDENTIALS_EXPIRED = 'CREDENTIALS_EXPIRED';
export const WEEKLY_BREAKDOWN_LIMIT_EXCEEDED =
    'WEEKLY_BREAKDOWN_LIMIT_EXCEEDED';
export const AB_TESTING_DATE_IS_NOT_INCLUDED =
    'AB_TESTING_DATE_IS_NOT_INCLUDED';
export const HEAVY_ARCHITECTURE_QUERY = 'HEAVY_ARCHITECTURE_QUERY';
export const CHANNEL_INVALID_FOR_OVERVIEW_WIDGET =
    'CHANNEL_INVALID_FOR_OVERVIEW_WIDGET';
export const ORGANICS_TOO_MANY_POSTS = 'ORGANICS_TOO_MANY_POSTS';
export const NO_FILE_MATCHING_SETTING = 'NO_FILE_MATCHING_SETTING';
export const TOO_MANY_DIMENSIONS_OR_METRICS = 'TOO_MANY_DIMENSIONS_OR_METRICS';

// new locale update - ing
export const ACCESS_TOKEN_INVALID = 'ACCESS_TOKEN_INVALID'; //
export const REFRESH_TOKEN_INVALID = 'REFRESH_TOKEN_INVALID'; //
export const ARCHITECTURE_NOT_SYNCED = 'ARCHITECTURE_NOT_SYNCED'; //
export const MISSING_DATE_REPORT_CACHE = 'MISSING_DATE_REPORT_CACHE'; //
export const DETAIL_ONLY_SUPPORT_ADACCOUNT_LEVEL_FOR_CACHE_ONLY_CHANNEL =
    'DETAIL_ONLY_SUPPORT_ADACCOUNT_LEVEL_FOR_CACHE_ONLY_CHANNEL'; //

export const INVALID_PUBLIC_CONNECTOR_USER = 'INVALID_PUBLIC_CONNECTOR_USER';
export const UNAUTHORIZED = 'UNAUTHORIZED';
export const INVALID_CREDENTIALS = 'INVALID_CREDENTIALS';
export const REQUIRED_CAMPAIGN_FILTER = 'REQUIRED_CAMPAIGN_FILTER';
export const INVALID_DIMENSIONS_METRICS_COMBINATION_WITH_DETAILS =
    'INVALID_DIMENSIONS_METRICS_COMBINATION_WITH_DETAILS';
export const FB_ORGANIC_MAX_PAGE_POST_COUNT = 'FB_ORGANIC_MAX_PAGE_POST_COUNT';
export const GA_ROW_LIMIT = 'GA_ROW_LIMIT';
export const MISSING_CREDENTIALS = 'MISSING_CREDENTIALS';
export const QUOTA_EXCEEDED = 'QUOTA_EXCEEDED';
export const UNSUPPORTED_BREAKDOWN_AS_PARENT =
    'UNSUPPORTED_BREAKDOWN_AS_PARENT';
export const EMPTY_RESPONSE = 'EMPTY_RESPONSE';
export const TOO_MANY_ACCOUNTS_IN_ONE_INPUT = 'TOO_MANY_ACCOUNTS_IN_ONE_INPUT';
export const ORGANIC_INCLUDE_ACTIVITY_POSTS = 'ORGANIC_INCLUDE_ACTIVITY_POSTS';
export const DEPRECATED_CONNECTOR = 'DEPRECATED_CONNECTOR';
export const DEPRECATED_CONNECTOR_ERROR = 'DEPRECATED_CONNECTOR_ERROR,';
export const BREAKDOWNS_MISSING = 'BREAKDOWNS_MISSING';

export const DASHBOARD_ERROR_CODE_INFO_MAP = {
    [SERVER_UNRESPONSIVE]: { type: ERROR },
    [USER_DELETED]: { type: ERROR },
    [EXTERNAL_SERVICE_UNAVAILABLE]: { type: WARNING },
    [EXTERNAL_SERVICE_UNAVAILABLE_CAUGHT]: { type: ERROR },
    [EXTERNAL_SERVICE_PERMISSION_MISSING]: { type: WARNING },
    [FEATURE_UNAVAILABLE]: { type: WARNING },
    [NEED_USER_ACTION]: { type: WARNING },
    [RETRY_LATTER]: { type: WARNING },
    [SHOULD_CHANGE_PARAMS]: { type: SHOULD_CHANGE_PARAMS },
    [FROM_DATE_TOO_OLD]: { type: ERROR },
    [INVALID_DIMENSIONS_METRICS_COMBINATION]: { type: ERROR },
    [INVALID_QUERY]: { type: ERROR },
    [NO_METRICS_AND_DIMENSIONS]: { type: ERROR },
    [METRICS_DIMENSIONS_NO_PREFIX]: { type: ERROR },
    [METRICS_NOT_AVAILABLE]: { type: ERROR },
    [RATE_LIMIT]: { type: WARNING },
    [UNKNOWN]: { type: WARNING },
    [FILTER_UNSUPPORTED]: { type: FILTER_UNSUPPORTED },
    [GSS_INVALID_SHEET_NAME]: { type: ERROR, canGoToConnection: true },
    [TOO_MUCH_DATA_REQUESTED]: { type: ERROR },
    [AIRTABLE_UNKNOWN_FIELD_NAME]: { type: ERROR },
    [MISSING_EXTERNAL_PERMISSIONS]: { type: ERROR },
    [INVALID_DATA_SOURCE]: { type: ERROR },
    [EXTERNAL_SERVICE_INTERNAL_ERROR]: { type: ERROR },
    [INVALID_CUSTOM_BREAKDOWN]: { type: ERROR },
    [TOO_MANY_ADS_ON_GA]: { type: ERROR },
    [TOKEN_ISSUE]: { type: ERROR, canGoToConnection: true },
    [DATE_SETTING_REQUIRED]: { type: ERROR },
    TOO_MANY_DATA_SOURCES: { type: WARNING },
    ECONNABORTED: { type: ERROR, canReload: true },
    INTERNAL_SERVER_ERROR: { type: ERROR },
    PAST_SHARED_UTM_CAMPAIGNS: { type: INFO },
    DIFFERENT_UTM_CAMPAIGNS: { type: WARNING },
    DELETED_CAMPAIGN: { type: INFO },
    DUPLICATED_ASSET: { type: ERROR },
    PAST_DIFFERENT_UTM_CAMPAIGNS: { type: INFO },
    SHARED_UTM_CAMPAIGNS: { type: WARNING },
    [CONNECTION_LOST]: { type: ERROR, canGoToConnection: true },
    [CONNECTION_LOST_WITHIN_7_DAYS]: { type: WARNING, canGoToConnection: true },
    SORT_FIELD_MISSING: { type: ERROR },

    [DEFAULT_ERROR]: { type: ERROR, canReload: true },

    [ACCESS_TOKEN_INVALID]: { type: ERROR, canGoToConnection: true }, //
    [REFRESH_TOKEN_INVALID]: { type: ERROR, canGoToConnection: true }, //
    [ARCHITECTURE_NOT_SYNCED]: { type: WARNING }, //
    [DATE_BREAKDOWN_UNSUPPORTED]: { type: ERROR },
    [MISSING_DATE_REPORT_CACHE]: { type: WARNING },
    [DETAIL_ONLY_SUPPORT_ADACCOUNT_LEVEL_FOR_CACHE_ONLY_CHANNEL]: {
        type: WARNING,
    },
    [DATE_RANGE_TOO_LONG]: { type: ERROR },
    [INVALID_DATE_FORMAT]: { type: ERROR },
    [INVALID_URL]: { type: ERROR },
    [MISSING_DATABASE_COLUMNS]: { type: ERROR },
    [INVALID_BREAKDOWN_FOR_CHART]: { type: ERROR },
    [TOO_MUCH_DATA_REQUESTED_INCOMPLETE]: { type: WARNING },
    [TOO_MUCH_DATA_REQUESTED_1_YEAR]: { type: WARNING },
    [INVALID_LEVEL_DIMENSION]: { type: ERROR },
    [CANT_SUM_METRIC]: { type: WARNING },
    [GA_SAMPLED_DATA]: { type: WARNING },
    [GA4_NO_ALPHA_CHARACTERS]: { type: ERROR },
    [SUM_TOO_BIG_TO_AGGREGATE]: { type: ERROR },
    [DATA_PRUNED]: { type: ERROR },
    [INACTIVE_USER_DATA_REMOVED]: { type: ERROR },
    [BREAKDOWN_NOT_AVAILABLE]: { type: ERROR },
    [RISK_DUPLICATED_DATA]: { type: WARNING },
    [REQUIRE_LEVEL_BREAKDOWN]: { type: ERROR },
    [CONNECT_SERVER_UNRESPONSIVE]: { type: ERROR },
    [REPORT_DATA_IS_STILL_FETCHING]: { type: WARNING },
    [INACTIVE_USER_DATA_NOT_AVAILABLE]: { type: WARNING },
    [TOO_MUCH_API_CALLS_REQUESTED]: { type: ERROR },
    [INVALID_DIMENSIONS_COMBINATION]: { type: ERROR },
    [EXCESSIVE_METRICS_SELECTED]: { type: ERROR },
    [CREDENTIALS_EXPIRED]: { type: ERROR },
    [WEEKLY_BREAKDOWN_LIMIT_EXCEEDED]: { type: WARNING },
    [AB_TESTING_DATE_IS_NOT_INCLUDED]: { type: WARNING },
    [HEAVY_ARCHITECTURE_QUERY]: { type: WARNING },
    [CUSTOM_BREAKDOWN_REGEX_NO_MATCH]: { type: WARNING },
    [CHANNEL_INVALID_FOR_OVERVIEW_WIDGET]: { type: ERROR },
    [ORGANICS_TOO_MANY_POSTS]: { type: WARNING },
    [NO_FILE_MATCHING_SETTING]: { type: ERROR },
    [TOO_MANY_DIMENSIONS_OR_METRICS]: { type: ERROR },
    [INVALID_PUBLIC_CONNECTOR_USER]: { type: ERROR },
    [UNAUTHORIZED]: { type: ERROR },
    [INVALID_CREDENTIALS]: { type: ERROR },
    [REQUIRED_CAMPAIGN_FILTER]: { type: ERROR },
    [INVALID_DIMENSIONS_METRICS_COMBINATION_WITH_DETAILS]: { type: ERROR },
    [FB_ORGANIC_MAX_PAGE_POST_COUNT]: { type: WARNING },
    [GA_ROW_LIMIT]: { type: WARNING },
    [MISSING_CREDENTIALS]: { type: ERROR },
    [QUOTA_EXCEEDED]: { type: ERROR },
    [UNSUPPORTED_BREAKDOWN_AS_PARENT]: { type: ERROR },
    [EMPTY_RESPONSE]: { type: ERROR },
    [TOO_MANY_ACCOUNTS_IN_ONE_INPUT]: { type: ERROR },
    [ORGANIC_INCLUDE_ACTIVITY_POSTS]: { type: WARNING },
    [DEPRECATED_CONNECTOR]: { type: WARNING },
    [DEPRECATED_CONNECTOR_ERROR]: { type: ERROR },
    [BREAKDOWNS_MISSING]: { type: WARNING },
};

/* 1st deploy list */
const SET_ERROR_CODES_TO_DISPLAY = new Set([
    INVALID_CUSTOM_BREAKDOWN,
    CUSTOM_BREAKDOWN_REGEX_NO_MATCH,
    TOO_MANY_ADS_ON_GA,
    TOO_MUCH_DATA_REQUESTED,
    INVALID_QUERY,
    TOKEN_ISSUE,
    RETRY_LATTER,
    EXTERNAL_SERVICE_UNAVAILABLE,
    INVALID_DIMENSIONS_METRICS_COMBINATION,
    NO_METRICS_AND_DIMENSIONS,
    RATE_LIMIT,
    DATE_SETTING_REQUIRED,
    GSS_INVALID_SHEET_NAME,
    FROM_DATE_TOO_OLD,
    ACCESS_TOKEN_INVALID,
    REFRESH_TOKEN_INVALID,
    ARCHITECTURE_NOT_SYNCED,
    DATE_BREAKDOWN_UNSUPPORTED,
    CONNECTION_LOST_WITHIN_7_DAYS,
    MISSING_DATE_REPORT_CACHE,
    DETAIL_ONLY_SUPPORT_ADACCOUNT_LEVEL_FOR_CACHE_ONLY_CHANNEL,
    METRICS_NOT_AVAILABLE,
    INTERNAL_SERVER_ERROR,
    TOO_MANY_DATA_SOURCES,
    INVALID_DATA_SOURCE,
    EXTERNAL_SERVICE_PERMISSION_MISSING,
    INVALID_DATE_FORMAT,
    INVALID_URL,
    MISSING_DATABASE_COLUMNS,
    USER_DELETED,
    INVALID_BREAKDOWN_FOR_CHART,
    TOO_MUCH_DATA_REQUESTED_INCOMPLETE,
    TOO_MUCH_DATA_REQUESTED_1_YEAR,
    INVALID_LEVEL_DIMENSION,
    CANT_SUM_METRIC,
    GA_SAMPLED_DATA,
    GA4_NO_ALPHA_CHARACTERS,
    SUM_TOO_BIG_TO_AGGREGATE,
    INACTIVE_USER_DATA_REMOVED,
    BREAKDOWN_NOT_AVAILABLE,
    RISK_DUPLICATED_DATA,
    REQUIRE_LEVEL_BREAKDOWN,
    CONNECT_SERVER_UNRESPONSIVE,
    REPORT_DATA_IS_STILL_FETCHING,
    INACTIVE_USER_DATA_NOT_AVAILABLE,
    TOO_MUCH_API_CALLS_REQUESTED,
    INVALID_DIMENSIONS_COMBINATION,
    EXCESSIVE_METRICS_SELECTED,
    CREDENTIALS_EXPIRED,
    WEEKLY_BREAKDOWN_LIMIT_EXCEEDED,
    AB_TESTING_DATE_IS_NOT_INCLUDED,
    HEAVY_ARCHITECTURE_QUERY,
    EXTERNAL_SERVICE_UNAVAILABLE_CAUGHT,
    CHANNEL_INVALID_FOR_OVERVIEW_WIDGET,
    ORGANICS_TOO_MANY_POSTS,
    NO_FILE_MATCHING_SETTING,
    TOO_MANY_DIMENSIONS_OR_METRICS,
    INVALID_PUBLIC_CONNECTOR_USER,
    EXTERNAL_SERVICE_INTERNAL_ERROR,
    UNAUTHORIZED,
    INVALID_CREDENTIALS,
    REQUIRED_CAMPAIGN_FILTER,
    INVALID_DIMENSIONS_METRICS_COMBINATION_WITH_DETAILS,
    FB_ORGANIC_MAX_PAGE_POST_COUNT,
    GA_ROW_LIMIT,
    MISSING_CREDENTIALS,
    QUOTA_EXCEEDED,
    UNSUPPORTED_BREAKDOWN_AS_PARENT,
    EMPTY_RESPONSE,
    TOO_MANY_ACCOUNTS_IN_ONE_INPUT,
    ORGANIC_INCLUDE_ACTIVITY_POSTS,
    DEPRECATED_CONNECTOR_ERROR,
    DEPRECATED_CONNECTOR,
    BREAKDOWNS_MISSING,
]);

export const SET_ERROR_CODES_NOT_TO_DISPLAY_DATA_ON_PANEL = new Set([
    'CUSTOM_METRIC_CIRCULAR_DEPENDENCY',
    'FILTERED_FIELDS',
    // INTERNAL_ERROR,
    // DEFAULT_ERROR,
]);

export const makeErrorInfoObj = ({
    error: errorFromBE,
    adAccount,
    curWidgetError,
    callback,
    displayedWidgetTitle,
}) => {
    const errorDetail = R.pathOr({}, ['response', 'data'], errorFromBE);

    let errorCode = R.prop('code')(errorDetail);
    !isValueExist(errorCode) && (errorCode = R.prop('code', errorFromBE));

    if (SET_ERROR_CODES_NOT_TO_DISPLAY_DATA_ON_PANEL.has(errorCode)) return {};

    if (!SET_ERROR_CODES_TO_DISPLAY.has(errorCode)) {
        errorCode = DEFAULT_ERROR;
    }

    const parsedErrorDetail = {
        ...DASHBOARD_ERROR_CODE_INFO_MAP[errorCode],
        ...errorDetail,
    };

    const adAccounts = R.compose(
        R.append(adAccount),
        R.propOr([], 'adAccounts')
    )(curWidgetError);

    const newErrorCodeToSourceList = createNewErrorCodeToSourceList({
        curWidgetError,
        parsedErrorDetail,
        errorCode,
        adAccount,
    });

    const data = {
        errors: {
            ...curWidgetError.errors,
            [errorCode]: parsedErrorDetail,
        },
        errorCodeToSourceList: newErrorCodeToSourceList,
        displayedWidgetTitle,
        callback,
        adAccounts,
    };

    return data;
};

const createNewErrorCodeToSourceList = function ({
    curWidgetError,
    parsedErrorDetail,
    errorCode,
    adAccount,
}) {
    // console.log('00:: currWidgetError: ', errorCode, curWidgetError);
    let errorCodeToSourceList = R.pathOr({}, ['errorCodeToSourceList'])(
        curWidgetError
    );

    // response > data > properties > dataSource missing => add adAccount( basically same but a little bit different)
    const updateParsedDataSource = R.pathOr(adAccount, [
        'properties',
        'dataSource',
    ])(parsedErrorDetail);
    parsedErrorDetail = R.assocPath(
        ['properties', 'dataSource'],
        updateParsedDataSource
    )(parsedErrorDetail);

    const newSourceList = R.compose(
        // TODO check:: what if asset is undefined: result to only 1 after uniqBy
        R.uniqBy(R.path(['properties', 'dataSource', 'id'])),
        R.concat([parsedErrorDetail]),
        R.pathOr([], [errorCode])
    )(errorCodeToSourceList);
    // console.log('2: newSourceList: ', newSourceList);

    errorCodeToSourceList = R.assocPath(
        [errorCode],
        newSourceList
    )(errorCodeToSourceList);
    // console.log('3: errorCodeToSourceList: ', errorCodeToSourceList);

    return errorCodeToSourceList;
};

export const checkIfWidgetHasDisconnectedDataSource = ({
    widget,
    dataSourcesDisconnectedWithin7Days,
}) => {
    if (!isValueExist(dataSourcesDisconnectedWithin7Days)) {
        return false;
    }

    const dataSourceIdsFromDisconnected = R.map(
        R.path(['properties', 'dataSource', 'assetId'])
    )(dataSourcesDisconnectedWithin7Days);

    const dataSourceIdsFromWidget = R.compose(
        R.pluck('assetId'),
        R.pathOr([], ['adAccounts'])
    )(widget);

    const disconnectedIdSet = new Set(dataSourceIdsFromDisconnected);
    for (const id of dataSourceIdsFromWidget) {
        if (disconnectedIdSet.has(id)) return true;
    }

    return false;
};

export const clearEmptyFilter = R.filter(isValueExist);

export const sortLongString = R.sort(R.comparator(R.gt));

export const refineViewLayout = ({ currentLayout, index, widgets, type }) => {
    const { ADD_TO_TOP, ADD_TO_BOTTOM, DUPLICATION } = WIDGET_ADD_TYPE;

    const widget = widgets[index];

    const getNewLayout = () =>
        R.compose(
            R.assoc('i', widget.id),
            (type) => VIEW_DEFAULT_LAYOUT_CONFIG[type],
            R.propOr('default', 'type')
        )(widget);

    switch (type) {
        case ADD_TO_TOP: {
            const newLayout = getNewLayout();

            const updatedLayout = currentLayout.map((layout) => {
                const h = typeof newLayout.h !== 'number' ? 0 : newLayout.h;

                return R.assoc('y', h + layout.y, layout);
            });
            return [newLayout, ...updatedLayout];
        }
        case ADD_TO_BOTTOM: {
            let newLayout = getNewLayout();

            let lastYValue = R.compose(
                R.propOr(0, 'y'),
                R.last,
                R.sort((a, b) => a.y - b.y)
            )(currentLayout);

            const isNotNumber = typeof lastYValue !== 'number';

            lastYValue = isNotNumber ? 0 : lastYValue;

            const updatedY =
                currentLayout.length > 0 ? lastYValue + newLayout.h : 0;

            newLayout = R.assoc('y', updatedY, newLayout);

            return [...currentLayout, newLayout];
        }
        case DUPLICATION: {
            const parentWidget = widgets[index - 1];

            const { id } = parentWidget;

            const parentWidgetLayout = R.find(R.propEq('i', id))(currentLayout);
            let { y = 0, h } = parentWidgetLayout;

            const isNotNumber = typeof y !== 'number';
            y = isNotNumber ? 0 : y;

            const newLayout = R.compose(
                R.assoc('y', y + h),
                R.assoc('i', widget.id),
                R.clone
            )(parentWidgetLayout);

            const layoutListHasHigherY = currentLayout.filter(
                (layout) => layout.y > y
            );

            const updatedLayout = currentLayout.map((layout) => {
                const hasTargetLayout = layoutListHasHigherY.some(
                    (_layout) => _layout.i === layout.i
                );

                return hasTargetLayout
                    ? R.assoc('y', layout.y + h, layout)
                    : layout;
            });

            return R.insert(index, newLayout, updatedLayout);
        }
    }
};

export const updateConversionName = memoizee(
    function (headers = [], isAnalytic) {
        return R.compose(
            R.reduce((acc, name) => {
                acc[name] = getFieldLocale.call(this, name);
                return acc;
            }, {}),
            R.filter((name) => {
                const { metric, tracker } = getConversionCellInfo(name);

                return (
                    (!!tracker && CONVERSION_TYPES.has(metric)) || isAnalytic
                );
            })
        )(headers);
    },
    { normalizer: ([headers = []]) => headers.join('') }
);

export const GA_TYPES = new Set([
    CONNECTION_TYPES.GA,
    CONNECTION_TYPES.GA4,
    'hubspotDeals',
    'appsFlyer',
    'googleAnalyticsMCF',
    'branch',
    'adjust',
    'singular',
]);

export const isBlendableSource = ({ isCustomReportAccount, is_analytic }) => {
    return isCustomReportAccount || is_analytic;
};
export const isMappableSource = ({ isFreeFormat = false }) => {
    return isFreeFormat;
};

export const refineChannelIconName = (channel) => {
    return channel === 'facebook'
        ? 'fbMulti'
        : channel === 'google'
        ? 'googleMulti'
        : channel;
};

export const isGss = (v = '') => v && String(v).startsWith('gss:');

export const maybePercent = (v = '') =>
    numeral(v)._value !== null && String(v).endsWith('%');

export const loadProductFruitsChatBot = () => {
    let intervalCheckLoading;

    // adJust position depending on to top button
    const adjustProductFruitChatbot = () => {
        const productFruitRoot = window.__pfruitsShadowRoot;

        if (!productFruitRoot) {
            return;
        }

        const productFruitChatbot =
            productFruitRoot.querySelector('.button-launcher');

        const productFruitIsReady = window.productFruitsIsReady;

        if (!productFruitChatbot || !productFruitIsReady) {
            return;
        }

        const toTopBtn =
            window.document.getElementsByClassName('to-top-wrapper')[0];

        try {
            if (productFruitChatbot && toTopBtn) {
                productFruitChatbot.style.setProperty(
                    'bottom',
                    '86px',
                    'important'
                );
                productFruitChatbot.style.setProperty(
                    'right',
                    '26px',
                    'important'
                );
            } else if (productFruitChatbot && !toTopBtn) {
                productFruitChatbot.style.setProperty(
                    'bottom',
                    '20px',
                    'important'
                );
                productFruitChatbot.style.setProperty(
                    'right',
                    '20px',
                    'important'
                );
            }
        } catch (e) {
            console.error('loadHubSpotChatBot', e);
        } finally {
            clearInterval(intervalCheckLoading);
        }
    };

    const productFruitIsReady = window.productFruitsIsReady;
    if (productFruitIsReady) {
        adjustProductFruitChatbot();
    } else {
        intervalCheckLoading = setInterval(adjustProductFruitChatbot, 250);
    }
};

export const hideHubSpotChatBot = () => {
    window?.HubSpotConversations?.widget.remove();
};

export const checkIsAnalyticSource = R.anyPass([
    R.prop('is_analytic'),
    R.prop('isCustomReportAccount'),
]);

export const ECOMMERCE_CHANNELS = new Set([
    'coupang',
    'cafe24',
    'naverCommerce',
    'shopify',
    'godomall',
    'shopee',
    'lazada',
    'kakaoShopping',
    'wooCommerce',
]);

export const checkIsPublic = R.propSatisfies(R.test(/Public/), 'channel');

export const checkIsGaSource = ({ channel = '' }) => GA_TYPES.has(channel);

export const checkSourceTableMode = memoize(
    function (sources = []) {
        const sourceMap = sources.reduce(
            (acc, source) => {
                if (checkIsGaSource(source)) {
                    acc.gas.push(source);

                    // settings(blending) is ignored at datastream
                    if (blendingActivated(source)) {
                        acc.blended.push(source);
                    }
                } else if (blendingActivated(source)) {
                    acc.blended.push(source);
                } else if (
                    !checkIsAnalyticSource(source) ||
                    mappingActivated(source)
                ) {
                    acc.marketing.push(source);
                } else {
                    acc.customs.push(source);
                }

                return acc;
            },
            {
                marketing: [],
                blended: [],
                customs: [],
                gas: [],
            }
        );

        const { marketing, customs, blended, gas } = sourceMap;
        const isAllBlended = sources.length === blended.length;
        const isAllGas = sources.length === gas.length;

        const isEmpty = !isValueExist(sources);
        const isCustomsExist = isValueExist(customs);
        const isBlendedExistAndNotAllBlended =
            isValueExist(blended) && !isAllBlended;
        const isGasExistAndAllGas = isValueExist(gas) && isAllGas;

        // can toggle 'analytic' and 'ads' when only blended sources are selected
        if (isEmpty || isAllBlended) {
            return 'toggleable';
        }

        if (
            !isValueExist(marketing) &&
            (isCustomsExist ||
                isBlendedExistAndNotAllBlended ||
                isGasExistAndAllGas)
        ) {
            return 'analytic';
        }

        return 'ads';
    },
    R.compose(
        R.join(':'),
        R.map(({ channelId, settings }) =>
            [channelId, JSON.stringify(settings)].join('::')
        ),
        R.defaultTo([])
    )
);

const hasJoinType = R.has('isFullJoin');

const hasBreakdownMapping = R.propSatisfies(isValueExist, 'breakdownMapping');
const hasDefaultTargetBreakdown = R.has('defaultTargetBreakdown');

const hasValidOldDataSetting = (setting = {}) => {
    const { dateField, dateFormat, requireDateSettings, isFullJoin } = setting;
    const blendDataSetting = R.pathOr({}, ['blendDataSettings', 0], setting);

    const isValidOldDateSetting =
        (requireDateSettings && Boolean(dateField && dateFormat)) ||
        !requireDateSettings;

    return (
        isValidOldDateSetting &&
        isFullJoin &&
        hasDefaultTargetBreakdown(blendDataSetting)
    );
};

export const hasValidBlendSetting = (settings = {}) => {
    if (!isValueExist(settings)) return false;

    const blendDataSettings = R.propOr([], 'blendDataSettings', settings);

    const isNewValidSetting =
        hasJoinType(settings) &&
        R.any(
            R.allPass([hasDefaultTargetBreakdown, hasBreakdownMapping]),
            blendDataSettings
        );

    const isOldValidSetting = hasValidOldDataSetting(settings);

    return isNewValidSetting || isOldValidSetting;
};
export const hasValidBlendingSetting = (source) => {
    return R.propSatisfies(hasValidBlendSetting, 'settings', source);
};
export const hasValidMappingSetting = (source) => {
    return R.pathSatisfies(
        isValueExist,
        ['advancedSettings', 'marketingAdapterSettings'],
        source
    );
};

export const blendingActivated = (source) => {
    if (!source) return false;
    const isMarketingAdapted = R.path(
        ['settings', 'isMarketingAdapted'],
        source
    );

    return !isMarketingAdapted && hasValidBlendingSetting(source);
};
export const mappingActivated = (source) => {
    if (!source) return false;
    const isMarketingAdapted = R.path(
        ['settings', 'isMarketingAdapted'],
        source
    );

    return isMarketingAdapted && hasValidMappingSetting(source);
};

export const GoalWidgetMode = {
    NORMAL: 'normal',
    FORECAST: 'forecast',
};

const GSS_URL = 'https://docs.google.com/spreadsheets/d/';

export const getGoogleSpreadSheetUrl = (item) => {
    const [gssId, sheetId] = R.compose(
        R.split('/'),
        R.propOr('', 'channelId'),
        R.defaultTo({})
    )(item);

    const mainUrl = GSS_URL + gssId;

    return mainUrl + (sheetId ? '/view#gid=' + sheetId : '');
};

export const isAppsFlyer = R.propEq('appsFlyer', 'channel');

export const DISPLAY_CHANNEL_NAME_FIRST_CHANNELS = [
    'csvWithDate',
    'googleSpreadSheet',
    'fileFromEmail',
    'fileFromSftp',
    'amazonS3',
    'csvWithUrl',
    'fileFromOneDrive',
    'csv',
    'postgres',
    'mysql',
    'mongodb',
    'redshift',
    'fileFromBox',
    'fileFromSharePoint',
];

export const DISPLAY_CHANNEL_NAME_CHANNELS = [
    'adverity',
    ...DISPLAY_CHANNEL_NAME_FIRST_CHANNELS,
];

export const refineChannel = ({
    channel,
    displayChannelName,
    dataSourceName,
    hasAlternativeChannelName,
    alternativeChannelName,
}) =>
    hasAlternativeChannelName
        ? alternativeChannelName || displayChannelName || channel
        : channel === 'airbyte'
        ? dataSourceName
        : channel;

/**
 * Names the entries of the filter widget's *first-level* group menu. Mapped
 * accounts collapse into `marketing`, and a free-format account is labelled by
 * its channel.
 *
 * This is NOT the rule a filter is saved under -- see `getSavedFilterGroupId`
 * below. A free-format group is not a leaf of this menu, so picking one opens a
 * second dropdown and the account's own `groupId` is what gets stored; the
 * channel label here never reaches the saved model. Use this for building menus
 * only.
 *
 * The local channel refinement below is not `refineChannel` above -- it ignores
 * `alternativeChannelName`. Moved here verbatim from the filter widget's
 * dropdown config; do not merge the two.
 */
export function getChannelOptionGroupId(account = {}) {
    const refineFreeFormatChannel = ({
        channel,
        displayChannelName,
        dataSourceName,
        hasAlternativeChannelName,
    } = {}) =>
        hasAlternativeChannelName
            ? displayChannelName || channel
            : channel === 'airbyte'
              ? dataSourceName
              : channel;

    if (mappingActivated(account)) {
        return 'marketing';
    }

    if (account?.isFreeFormat) {
        return refineFreeFormatChannel(account);
    }

    return account?.groupId;
}

/**
 * The `groupId` a saved filter is actually stored under. Two branches only: a
 * mapped account is offered under `marketing`, everything else keeps its own
 * `account.groupId`.
 *
 * Kept separate from `getChannelOptionGroupId` above on purpose. Reading the
 * saved model through the menu rule buckets free-format accounts under their
 * channel label instead of the groupId they were stored with, which loses them.
 */
export function getSavedFilterGroupId(account = {}) {
    return mappingActivated(account) ? 'marketing' : account?.groupId;
}
